import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Trash2, Pencil, Check, X, BookCopy, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from './axiosConfig';
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';
import { swalTheme } from '../lib/swalTheme';

const CLAUSE_TYPES = ['GENERAL', 'PAYMENT', 'TERMINATION', 'WARRANTY', 'LIABILITY'];
const EMPTY_ROW = { id: null, template_code: '', title: '', clause_type: 'GENERAL', description: '' };

const ContractTermTemplateList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const { fontSize } = useFontSize();
  const { canCreate, canUpdate, canDelete } = usePermissions('contracts');

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [clauseFilter, setClauseFilter] = useState('');
  const [editingId, setEditingId] = useState(null); // null | 'new' | row.id
  const [draft, setDraft] = useState(EMPTY_ROW);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/contract-term-templates', { params: { q: search, clause_type: clauseFilter || undefined, limit: 200 } });
      setData(res.data.data || []);
    } catch (err) {
      toast.error(t('contract.messages.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [search, clauseFilter, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = (row) => { setEditingId(row.id); setDraft({ ...row }); };
  const startNew = () => { setEditingId('new'); setDraft(EMPTY_ROW); };
  const cancelEdit = () => { setEditingId(null); setDraft(EMPTY_ROW); };

  const saveDraft = async () => {
    if (!draft.template_code?.trim() || !draft.title?.trim() || !draft.description?.trim()) {
      return toast.error(t('contractTemplates.messages.required'));
    }
    setSaving(true);
    try {
      await api.post('/contract-term-templates/save', draft);
      toast.success(t('common.save_success'));
      cancelEdit();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.save_fail'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const result = await Swal.fire({
      ...swalTheme(),
      title: t('contract.swal.deleteTitle'),
      text: t('contract.swal.deleteText'),
      icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33',
      confirmButtonText: t('common.yes'), cancelButtonText: t('common.cancel')
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/contract-term-templates/${row.id}`);
      toast.success(t('common.delete_success'));
      fetchData();
    } catch (err) {
      toast.error(t('common.delete_fail'));
    }
  };

  const clauseLabel = (v) => t(`contract.options.${v.toLowerCase()}`, v);

  return (
    <div className={`p-6 max-w-6xl mx-auto`} style={{ fontSize: `${fontSize}px` }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-primary text-white p-2.5 rounded-lg shadow-md"><BookCopy size={20} /></div>
          <div>
            <h1 className="font-black text-primary-dark text-lg tracking-tight">{t('contractTemplates.title')}</h1>
            <p className="text-ink-secondary text-[0.85em] font-bold">{t('contractTemplates.subtitle')}</p>
          </div>
        </div>
        {canCreate && editingId === null && (
          <button
            onClick={startNew}
            className="bg-primary text-white px-6 py-2.5 rounded-sm font-black text-[0.85em] flex items-center gap-2 hover:bg-black transition-all shadow-md active:scale-95 uppercase"
          >
            <Plus size={16} /> {t('contractTemplates.actions.add')}
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className={`absolute top-1/2 -translate-y-1/2 text-ink-secondary ${isRTL ? 'right-3' : 'left-3'}`} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className={`w-full border border-line rounded-sm py-2 text-[0.92em] font-bold outline-none focus:border-primary bg-surface-card ${isRTL ? 'pr-8 text-right' : 'pl-8'}`}
          />
        </div>
        <select
          value={clauseFilter}
          onChange={(e) => setClauseFilter(e.target.value)}
          className="border border-line rounded-sm py-2 px-3 text-[0.85em] font-black uppercase outline-none focus:border-primary bg-surface-card"
        >
          <option value="">{t('common.all')}</option>
          {CLAUSE_TYPES.map(ct => <option key={ct} value={ct}>{clauseLabel(ct)}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-surface-card border-2 border-line rounded shadow-2xl overflow-hidden">
        <table className={`w-full ${isRTL ? 'text-right' : 'text-left'}`}>
          <thead className="bg-surface-panel border-b text-ink-secondary text-[0.77em] font-black uppercase tracking-widest">
            <tr>
              <th className={`p-3 border-x w-40 ${isRTL ? 'border-l' : 'border-r'}`}>{t('contractTemplates.fields.code')}</th>
              <th className={`p-3 border-x w-36 text-center ${isRTL ? 'border-l' : 'border-r'}`}>{t('contract.tables.clauseType')}</th>
              <th className={`p-3 border-x w-56 ${isRTL ? 'border-l' : 'border-r'}`}>{t('contractTemplates.fields.templateTitle')}</th>
              <th className={`p-3 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{t('contract.tables.clauseDesc')}</th>
              <th className="p-3 w-24 text-center">{t('common.actions', 'Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-subtle font-bold">
            {editingId === 'new' && (
              <TemplateRow draft={draft} setDraft={setDraft} isRTL={isRTL} isEditing onSave={saveDraft} onCancel={cancelEdit} saving={saving} clauseLabel={clauseLabel} />
            )}

            {loading ? (
              <tr><td colSpan="5" className="p-16 text-center"><Loader2 className="animate-spin inline text-primary" size={22} /></td></tr>
            ) : data.length === 0 && editingId !== 'new' ? (
              <tr><td colSpan="5" className="p-16 text-center italic text-ink-secondary font-black uppercase tracking-widest text-[0.85em]">{t('contractTemplates.messages.empty')}</td></tr>
            ) : (
              data.map(row => (
                editingId === row.id ? (
                  <TemplateRow key={row.id} draft={draft} setDraft={setDraft} isRTL={isRTL} isEditing onSave={saveDraft} onCancel={cancelEdit} saving={saving} clauseLabel={clauseLabel} />
                ) : (
                  <tr key={row.id} className="hover:bg-primary-soft transition-colors">
                    <td className={`p-3 border-x font-black text-primary-dark italic ${isRTL ? 'border-l' : 'border-r'}`}>{row.template_code}</td>
                    <td className={`p-3 border-x text-center ${isRTL ? 'border-l' : 'border-r'}`}>
                      <span className="text-[0.69em] font-black uppercase text-status-warning bg-status-warning-bg/10 px-2 py-1 rounded">{clauseLabel(row.clause_type)}</span>
                    </td>
                    <td className={`p-3 border-x ${isRTL ? 'border-l' : 'border-r'}`}>{row.title}</td>
                    <td className={`p-3 border-x text-ink-secondary font-normal truncate max-w-md ${isRTL ? 'border-l' : 'border-r'}`}>{row.description}</td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        {canUpdate && <button onClick={() => startEdit(row)}><Pencil size={15} className="text-ink-secondary hover:text-primary transition-colors" /></button>}
                        {canDelete && <button onClick={() => handleDelete(row)}><Trash2 size={15} className="text-ink-secondary hover:text-status-danger transition-colors" /></button>}
                      </div>
                    </td>
                  </tr>
                )
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const TemplateRow = ({ draft, setDraft, isRTL, onSave, onCancel, saving, clauseLabel }) => (
  <tr className="bg-primary-soft/30">
    <td className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
      <input
        value={draft.template_code}
        onChange={e => setDraft({ ...draft, template_code: e.target.value.toUpperCase() })}
        className="w-full bg-white border border-line rounded-sm px-2 py-1.5 outline-none focus:border-primary font-black text-primary-dark"
        placeholder="PAY-30"
      />
    </td>
    <td className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
      <select
        value={draft.clause_type}
        onChange={e => setDraft({ ...draft, clause_type: e.target.value })}
        className="w-full bg-white border border-line rounded-sm px-2 py-1.5 outline-none focus:border-primary font-black uppercase"
      >
        {CLAUSE_TYPES.map(ct => <option key={ct} value={ct}>{clauseLabel(ct)}</option>)}
      </select>
    </td>
    <td className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
      <input
        value={draft.title}
        onChange={e => setDraft({ ...draft, title: e.target.value })}
        className="w-full bg-white border border-line rounded-sm px-2 py-1.5 outline-none focus:border-primary font-black"
      />
    </td>
    <td className={`p-2 border-x ${isRTL ? 'border-l' : 'border-r'}`}>
      <textarea
        value={draft.description}
        onChange={e => setDraft({ ...draft, description: e.target.value })}
        className="w-full bg-white border border-line rounded-sm px-2 py-1.5 outline-none focus:border-primary font-normal resize-y min-h-[38px]"
      />
    </td>
    <td className="p-2 text-center">
      <div className="flex items-center justify-center gap-2">
        <button disabled={saving} onClick={onSave}><Check size={17} className="text-status-success hover:scale-110 transition-transform" /></button>
        <button disabled={saving} onClick={onCancel}><X size={17} className="text-ink-secondary hover:text-status-danger transition-colors" /></button>
      </div>
    </td>
  </tr>
);

export default ContractTermTemplateList;
